import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';
import AssignmentList from './AssignmentList';
import CollapsibleAssignmentHeader from './CollapsibleAssignmentHeader';
import { totalsByName } from './assignmentUtils';
import { useAuth } from '../../contexts/AuthContext';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EquipAssignmentSection({ jobId, assignments, equipment, canModify, onReload, onEdit }) {
  const [form, setForm] = useState({ equipment_id: '', date: '', hours: '' });
  const [expanded, setExpanded] = useState(false);
  const billableEquipment = equipment.filter((e) => e.billable !== false);
  const { user } = useAuth();
  const [availability, setAvailability] = useState({}), [checking, setChecking] = useState(false), [availabilityError, setAvailabilityError] = useState(''), [retry, setRetry] = useState(0);
  const [checkedDate, setCheckedDate] = useState('');
  const hasDate = /^\d{4}-\d{2}-\d{2}$/.test(form.date);
  const availabilityReady = hasDate && checkedDate === form.date && !checking && !availabilityError;
  const canAssign = !!form.equipment_id && availabilityReady && availability[form.equipment_id]?.available;
  const requestId = useRef(0);
  useEffect(() => {
    const current = ++requestId.current;
    setAvailability({}); setAvailabilityError(''); setCheckedDate('');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) { setChecking(false); return; }
    setChecking(true);
    axios.get(`${API}/equipment/availability`, { params: { date: form.date } }).then((r) => {
      if (current === requestId.current) {
        setAvailability(Object.fromEntries(r.data.map((row) => [row.equipment_id, row])));
        setCheckedDate(form.date);
      }
    }).catch((e) => { if (current === requestId.current) setAvailabilityError(extractErrorMessage(e, 'Unable to check equipment availability')); })
      .finally(() => { if (current === requestId.current) setChecking(false); });
    return () => { requestId.current += 1; };
  }, [form.date, retry]);

  const { grandTotal, perName } = totalsByName(assignments, 'equipment_name');

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.equipment_id || !form.date) { toast.error('Select equipment and date'); return; }
    if (!canAssign) { toast.error('Equipment availability must be confirmed for the selected date'); return; }
    try {
      const payload = { 
        job_id: jobId, 
        equipment_id: form.equipment_id,
        date: form.date,
        hours: form.hours ? parseFloat(form.hours) : null
      };
      await axios.post(`${API}/equipment-assignments`, payload);
      setForm({ equipment_id: '', date: '', hours: '' });
      onReload();
      toast.success('Assigned');
    } catch (error) { toast.error(extractErrorMessage(error, 'Unable to assign equipment')); setRetry((v) => v + 1); }
  };

  const handleRemove = async (id) => {
    try { await axios.delete(`${API}/equipment-assignments/${id}`); onReload(); toast.success('Removed'); }
    catch (error) { toast.error('Failed'); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="equip-assignments-section">
      <CollapsibleAssignmentHeader
        title="Equipment Assignments"
        icon={Wrench}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        count={assignments.length}
        totalHours={grandTotal}
        perName={perName}
        testid="equip-assignments"
      />
      {expanded && (
        <div className="mt-4">
          {canModify && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-slate-50 rounded-lg" data-testid="equip-assign-form">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="min-w-0">
                  <Label htmlFor="equip-select" className="text-xs text-slate-500">Equipment</Label>
                  <select id="equip-select" data-testid="equip-select" value={form.equipment_id} onChange={(e) => setForm({...form, equipment_id: e.target.value})}
                    className="h-9 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-base shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 disabled:cursor-not-allowed disabled:opacity-50">
                    <option value="">Select equipment</option>
                    {billableEquipment.map(e => {
                      const unavailableStatus = e.status && e.status !== 'available';
                      const unavailableDate = availabilityReady && !availability[e.id]?.available;
                      const reason = availabilityReady ? availability[e.id]?.reason || (unavailableDate ? 'Unavailable for this date' : '')
                        : unavailableStatus ? (e.status === 'maintenance' ? 'Under maintenance' : 'Currently in use') : '';
                      return <option key={e.id} value={e.id} data-testid={`equip-option-${e.id}`} disabled={!!unavailableStatus || !!unavailableDate}>{e.name}{reason ? ` — ${reason}` : ''}</option>;
                    })}
                  </select>
                </div>
                <div className="min-w-0" data-testid="equip-date"><Label htmlFor="equip-date-input" className="text-xs text-slate-500">Date</Label><Input id="equip-date-input" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="min-w-0 max-w-full text-base md:text-base" data-testid="equip-date-input" /></div>
                <div className="min-w-0">
                  <Label htmlFor="equip-hours" className="text-xs text-slate-500">Hours</Label>
                  <Input 
                    id="equip-hours"
                    type="number" 
                    inputMode="decimal"
                    className="min-w-0 text-base md:text-base"
                    step="0.25" 
                    min="0" 
                    placeholder="e.g. 2.5" 
                    value={form.hours} 
                    onChange={(e) => setForm({...form, hours: e.target.value})} 
                    data-testid="equip-hours" 
                  />
                </div>
                <Button type="submit" className="bg-slate-900" data-testid="equip-assign-btn" disabled={!canAssign}><Wrench size={16} className="mr-1" />Assign</Button>
              </div>
              {availabilityError && <div role="alert" className="text-sm text-red-700 mt-3" data-testid="equip-availability-error">{availabilityError}<Button type="button" variant="link" data-testid="equip-availability-retry" onClick={() => setRetry((v) => v + 1)}>Retry</Button></div>}
              {checking && <p role="status" className="text-sm text-slate-500 mt-3" data-testid="equip-availability-checking">Checking availability…</p>}
              {form.equipment_id && availabilityReady && !availability[form.equipment_id]?.available && <p className="text-sm text-amber-800 mt-3" data-testid="equip-availability-warning">{availability[form.equipment_id]?.reason || 'Equipment is unavailable on this date'}</p>}
            </form>
          )}
          <AssignmentList items={assignments} canModify={canModify} currentUserId={user?.id} onEdit={(a) => onEdit(a, 'equip')} onRemove={handleRemove} icon={Wrench} nameKey="equipment_name" />
        </div>
      )}
    </div>
  );
}
